// =============================================================================
// src/views/impostometro/impostometro-view.ts — AppModule + controller da Tela 08.
//
// Proveniencia: assets/unified-app.js linhas 1865-2030 (Sections.impostometro).
//   Porte FIEL do mapa-split arrecadacao(verde)/retorno(azul), do contador
//   flutuante, do resumo, do ranking dinamico e do insight. Comportamento,
//   tempos e ordem de chamadas preservados verbatim. NAO corrigir bugs aparentes;
//   desvios documentados no resumo final.
//
// SRP: este arquivo orquestra ativacao/troca de tabs; a marcacao mora em
//   impostometro-markup.ts e os helpers de DOM em impostometro-dom.ts.
// =============================================================================

import type {
  AppContext,
  MapController,
  FiscalYearEntry,
  FiscalYear,
  TaxKind,
  ZonaKey,
  HexColor,
} from '../../app/types';
import type { SectionLifecycleModule } from '../../core/section-manager';
// PathOptions nao e re-exportado por src/app/types.ts (e tipo interno do contrato
// MapController). Importamos direto de @types/leaflet, a mesma fonte usada la.
// Verificado via Context7 (/leaflet/leaflet) em 2026-05-30: L.PathOptions e a
// superficie estavel de estilo de path (fillColor/fillOpacity/weight/color/opacity).
import type { PathOptions } from 'leaflet';
import {
  MANAUS_FISCAL,
  MANAUS_ZONA_DISPLAY_NAME,
  pickColorFromRamp,
} from '../../data';
import {
  renderImpostometroSection,
  buildCounterOverlayHtml,
} from './impostometro-markup';
import {
  queryImpostometroTabs,
  setActiveTab,
  bindTabListenerOnce,
} from './impostometro-dom';

// ── Constantes de porte (assets/unified-app.js 1869-1872) ─────────────────────

/** Rampa verde para a arrecadacao (lado esquerdo do split). */
const GREEN_RAMP: readonly HexColor[] = [
  '#EAF3DE', '#C0DD97', '#97C459', '#639922', '#3B6D11', '#0F6E56',
];

/** Rampa azul para o retorno (lado direito do split). */
const BLUE_RAMP: readonly HexColor[] = [
  '#E6F1FB', '#85B7EB', '#5EA3E8', '#378ADD', '#185FA5', '#0C447C',
];

/**
 * Ordem das zonas usada para min/max e ranking. Identica ao array `zoneNames`
 * original (assets/unified-app.js 1871) — NAO ordenada alfabeticamente de
 * proposito; preserva o comportamento de Math.min/max sobre a sequencia.
 */
const FISCAL_ZONE_ORDER: readonly ZonaKey[] = [
  'norte', 'leste', 'centro-oeste', 'centro-sul', 'oeste', 'sul',
];

/** Ids dos overlays injetados em #mapOverlays, removidos no deactivate. */
const OVERLAY_IDS: readonly string[] = [
  'uo-counter', 'u-split-header-left', 'u-split-header-right', 'uo-split-divider',
];

/** Estilo de path comum aos dois lados do split (mesma borda/opacidade). */
function splitZonePathStyle(fillColor: HexColor): PathOptions {
  return {
    fillColor,
    fillOpacity: 0.75,
    weight: 0.8,
    color: 'rgba(255,255,255,0.6)',
    opacity: 1,
  };
}

/** Extrai os valores de uma zona-amounts na ordem fiscal canonica. */
function amountsInZoneOrder(amounts: Record<ZonaKey, number>): number[] {
  return FISCAL_ZONE_ORDER.map((zona) => amounts[zona]);
}

/**
 * Controlador da secao Impostometro. Mantem o tributo/ano correntes e re-renderiza
 * split, contador, resumo, ranking e insight. Espelha o objeto literal original
 * (Sections.impostometro) com estado mutavel privado e metodos curtos.
 */
class ImpostometroController {
  private currentTribute: TaxKind = 'icms';
  private currentYear: FiscalYear = 2025;
  private overlayIds: readonly string[] = [];

  /** Entrada fiscal corrente (DATA.fiscal[tributo][ano]). */
  private currentEntry(): FiscalYearEntry {
    return MANAUS_FISCAL[this.currentTribute][this.currentYear];
  }

  /**
   * Ativa a secao: aplica split, monta contador, liga controles e atualiza
   * resumo/ranking/insight — mesma ordem de _activate (linhas 1874-1882).
   */
  activate(mapCtrl: MapController): void {
    this.applySplitStyles(mapCtrl);
    this.buildCounterOverlay();
    this.bindControls(mapCtrl);
    this.updateSummary();
    this.updateRanking();
    this.updateInsight();
  }

  /**
   * Aplica o split verde/azul ao mapa e atualiza os headers per-capita.
   * Provenience: _applySplitStyles (linhas 1884-1912).
   */
  private applySplitStyles(mapCtrl: MapController): void {
    const entry = this.currentEntry();
    const arrecVals = amountsInZoneOrder(entry.arrec);
    const retornoVals = amountsInZoneOrder(entry.retorno);
    const arrecMin = Math.min(...arrecVals);
    const arrecMax = Math.max(...arrecVals);
    const retornoMin = Math.min(...retornoVals);
    const retornoMax = Math.max(...retornoVals);

    mapCtrl.setSplitMode(
      true,
      (zona) =>
        splitZonePathStyle(
          pickColorFromRamp(entry.arrec[zona], arrecMin, arrecMax, GREEN_RAMP),
        ),
      (zona) =>
        splitZonePathStyle(
          pickColorFromRamp(entry.retorno[zona], retornoMin, retornoMax, BLUE_RAMP),
        ),
    );
    this.refreshSplitHeaders();
  }

  /** Reescreve os headers do split com o rotulo "R$/hab" (linhas 1903-1911). */
  private refreshSplitHeaders(): void {
    const leftHeader = document.getElementById('u-split-header-left');
    const rightHeader = document.getElementById('u-split-header-right');
    if (leftHeader) {
      leftHeader.innerHTML =
        '<i class="ti ti-arrow-up-right" style="color:#0F6E56"></i> <strong>Arrecadação</strong> per capita &nbsp;<span style="font-size:9px;color:#888">R$/hab</span>';
    }
    if (rightHeader) {
      rightHeader.innerHTML =
        '<i class="ti ti-arrow-down-left" style="color:#185FA5"></i> <strong>Retorno</strong> / investimento &nbsp;<span style="font-size:9px;color:#888">R$/hab</span>';
    }
  }

  /** Injeta o overlay (headers + divisor + contador) em #mapOverlays (1914-1929). */
  private buildCounterOverlay(): void {
    const entry = this.currentEntry();
    const html = buildCounterOverlayHtml(entry, this.currentTribute, this.currentYear);
    const overlays = document.getElementById('mapOverlays');
    if (!overlays) return;
    overlays.insertAdjacentHTML('beforeend', html);
    this.overlayIds = OVERLAY_IDS;
  }

  /**
   * Atualiza o tick do contador flutuante (label/valor/sub) sem remontar o
   * overlay. Porte fiel de _updateCounter (linhas 1932-1939): o "tick" do
   * contador acontece a cada troca de tributo/ano, nao por timer.
   */
  private updateCounter(): void {
    const entry = this.currentEntry();
    const label = document.getElementById('uo-cf-label');
    const value = document.getElementById('uo-cf-value');
    const sub = document.getElementById('uo-cf-sub');
    if (label) {
      label.textContent = `${this.currentTribute === 'icms' ? 'ICMS' : 'ISS'} arrecadado · ${this.currentYear}`;
    }
    if (value) value.textContent = entry.total;
    if (sub) sub.innerHTML = yoyDeltaHtml(entry);
  }

  /** Liga as tabs de tributo e de ano (porte de _bindControls, 1942-1968). */
  private bindControls(mapCtrl: MapController): void {
    queryImpostometroTabs('.u-tributo-tab').forEach((tab) => {
      bindTabListenerOnce(tab, 'click', 'impostometro-tributo', () =>
        this.onTributeTab(mapCtrl, tab),
      );
    });
    queryImpostometroTabs('.u-year-tab').forEach((tab) => {
      bindTabListenerOnce(tab, 'click', 'impostometro-year', () =>
        this.onYearTab(mapCtrl, tab),
      );
    });
    // Reforma 2026-06 (F4): syncYearTabs (porte de 1942-1968, pintura inline
    // das tabs) foi APOSENTADO — .u-year-tab/.active em observatory.css pinta
    // via classe; setActiveTab ja alterna a classe no clique.
  }

  /** Handler de clique numa tab de tributo (linhas 1945-1953). */
  private onTributeTab(mapCtrl: MapController, tab: Element): void {
    setActiveTab(queryImpostometroTabs('.u-tributo-tab'), tab);
    this.currentTribute = readTributeFromTab(tab);
    this.applySplitStyles(mapCtrl);
    this.updateCounter();
    this.updateSummary();
    this.updateRanking();
    this.updateInsight();
  }

  /** Handler de clique numa tab de ano (linhas 1956-1965). */
  private onYearTab(mapCtrl: MapController, tab: Element): void {
    setActiveTab(queryImpostometroTabs('.u-year-tab'), tab);
    this.currentYear = readYearFromTab(tab);
    this.applySplitStyles(mapCtrl);
    this.updateCounter();
    this.updateSummary();
    this.updateRanking();
    this.updateInsight();
  }

  /** Atualiza os 3 cards do resumo (total/per capita/yoy) — linhas 1970-1976. */
  private updateSummary(): void {
    const entry = this.currentEntry();
    setTextById('u-imp-total', entry.total);
    setTextById('u-imp-percapita', entry.perCapita);
    setHtmlById('u-imp-yoy', yoyDeltaHtml(entry));
  }

  /**
   * Reconstroi o ranking de arrecadacao por zona (porte de _updateRanking,
   * 1978-2000). Ordena desc por arrecadacao, calcula barra %, gap retorno/arrec
   * e cor da rampa verde. Substitui o ranking estatico de render().
   */
  private updateRanking(): void {
    const entry = this.currentEntry();
    const container = document.getElementById('u-ranking-rows');
    if (!container) return;
    const arrecVals = amountsInZoneOrder(entry.arrec);
    const arrecMin = Math.min(...arrecVals);
    const arrecMax = Math.max(...arrecVals);
    const sorted = [...FISCAL_ZONE_ORDER].sort(
      (a, b) => entry.arrec[b] - entry.arrec[a],
    );
    container.innerHTML = sorted
      .map((zona, index) =>
        rankingRowHtml(entry, zona, index, arrecMin, arrecMax),
      )
      .join('');
  }

  /**
   * Atualiza o texto do insight de equidade fiscal (porte de _updateInsight,
   * 2002-2011): elege a zona com maior gap retorno/arrecadacao e narra o dado.
   */
  private updateInsight(): void {
    const entry = this.currentEntry();
    const el = document.getElementById('u-imp-insight-text');
    if (!entry || !el) return;
    const bestGapZone = [...FISCAL_ZONE_ORDER].sort(
      (a, b) => zoneGapRatio(entry, b) - zoneGapRatio(entry, a),
    )[0];
    /* v8 ignore next -- FISCAL_ZONE_ORDER nunca vazio; [0] sempre definido; guarda noUncheckedIndexedAccess */
    if (bestGapZone === undefined) return;
    const gap = Math.round(zoneGapRatio(entry, bestGapZone) * 100);
    el.textContent = `Em ${this.currentYear}, ${MANAUS_ZONA_DISPLAY_NAME[bestGapZone]} tem o maior gap retorno/arrecadação (+${gap}%). O dado sustenta narrativa de equidade fiscal e priorização territorial.`;
  }


  /**
   * Desativa a secao: encerra o split e remove os overlays (porte de deactivate,
   * 2022-2029). O original chama o singleton global MapController.setSplitMode(false);
   * aqui recebemos o controlador via DI (ver desvio no resumo).
   */
  deactivate(mapCtrl: MapController): void {
    mapCtrl.setSplitMode(false, noopZoneStyle, noopZoneStyle);
    this.overlayIds.forEach((overlayId) => {
      document.getElementById(overlayId)?.remove();
    });
    this.overlayIds = [];
  }
}

/** Razao de gap (retorno-arrecadacao)/arrecadacao de uma zona (helper DRY). */
function zoneGapRatio(entry: FiscalYearEntry, zona: ZonaKey): number {
  return (entry.retorno[zona] - entry.arrec[zona]) / entry.arrec[zona];
}

/** HTML de uma linha do ranking dinamico (porte verbatim de 1992-1998). */
function rankingRowHtml(
  entry: FiscalYearEntry,
  zona: ZonaKey,
  index: number,
  arrecMin: number,
  arrecMax: number,
): string {
  const pct = ((entry.arrec[zona] / arrecMax) * 100).toFixed(0);
  const gapPct = (zoneGapRatio(entry, zona) * 100).toFixed(0);
  const color = pickColorFromRamp(entry.arrec[zona], arrecMin, arrecMax, GREEN_RAMP);
  const gapClass =
    parseInt(gapPct) >= 0 ? 'u-rank-gap negative' : 'u-rank-gap positive';
  const gapSign = parseInt(gapPct) >= 0 ? '+' : '';
  return (
    '<div class="u-ranking-row">' +
    `<span class="u-rank-pos">${index + 1}</span>` +
    `<span class="u-rank-name">${MANAUS_ZONA_DISPLAY_NAME[zona]}</span>` +
    `<div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:${pct}%;background:${color}"></div></div>` +
    `<span class="u-rank-value" style="color:${color}">R$ ${entry.arrec[zona].toLocaleString('pt-BR')}</span>` +
    `<span class="${gapClass}">${gapSign}${gapPct}%</span>` +
    '</div>'
  );
}

/** Estilo no-op usado ao desligar o split (o controlador ignora ao desabilitar). */
function noopZoneStyle(): PathOptions {
  return {};
}

/** Le e valida o tributo de uma tab (data-tributo deve ser 'icms'|'iss'). */
function readTributeFromTab(tab: Element): TaxKind {
  const raw = (tab as HTMLElement).dataset['tributo'];
  if (raw !== 'icms' && raw !== 'iss') {
    throw new Error(
      `tab de tributo invalida: data-tributo recebido ${JSON.stringify(raw)}, esperado 'icms' | 'iss'`,
    );
  }
  return raw;
}

/** Conjunto de anos fiscais validos para data-year (paridade com FiscalYear). */
const VALID_FISCAL_YEARS: readonly FiscalYear[] = [
  2020, 2021, 2022, 2023, 2024, 2025,
];

/** Le e valida o ano de uma tab (data-year deve ser um FiscalYear conhecido). */
function readYearFromTab(tab: Element): FiscalYear {
  const raw = (tab as HTMLElement).dataset['year'];
  const parsed = Number(raw) as FiscalYear;
  if (!VALID_FISCAL_YEARS.includes(parsed)) {
    throw new Error(
      `tab de ano invalida: data-year recebido ${JSON.stringify(raw)}, esperado um de ${JSON.stringify(VALID_FISCAL_YEARS)}`,
    );
  }
  return parsed;
}

/** Atribui textContent a um elemento por id, no-op se ausente (DRY do guard). */
function setTextById(elementId: string, text: string): void {
  const element = document.getElementById(elementId);
  if (element) element.textContent = text;
}

/** Atribui innerHTML a um elemento por id, no-op se ausente (par de setTextById). */
function setHtmlById(elementId: string, html: string): void {
  const element = document.getElementById(elementId);
  if (element) element.innerHTML = html;
}

/**
 * Delta YoY com icone Tabler pela direcao (yoyClass). Reforma 2026-06 (F4):
 * a seta unicode saiu da STRING de dado (manaus-fiscal) — direcao e
 * apresentacao, nao dado.
 *
 * @example yoyDeltaHtml({ yoy: '6,2% vs 2024', yoyClass: 'up', … })
 *          // '<i class="ti ti-trending-up"…></i> 6,2% vs 2024'
 */
export function yoyDeltaHtml(entry: { yoy: string; yoyClass: 'up' | 'down' }): string {
  const icon = entry.yoyClass === 'up' ? 'ti-trending-up' : 'ti-trending-down';
  return `<i class="ti ${icon}" style="font-size:0.85em" aria-hidden="true"></i> ${entry.yoy}`;
}

/** Instancia unica do controlador (espelha o objeto literal singleton original). */
export const impostometroController = new ImpostometroController();

/** Ultimo mapa visto em activate, para deactivate() (sem args) repassar ao controlador. */
let impostometroActiveMap: MapController | null = null;

/**
 * Modulo de view do Impostometro (SectionLifecycleModule). `render()` emite a secao
 * estatica; `activate(ctx)`/`deactivate()` sao disparados pela SectionManager no
 * scroll-spy (porte do ciclo Sections.impostometro.activate/deactivate); `init(ctx)`
 * cobre o bootstrap inicial. Guardamos ctx.map para o deactivate sem-args.
 */
export const impostometroView: SectionLifecycleModule = {
  id: 'impostometro',
  render: renderImpostometroSection,
  // A ativacao e dirigida SO pelo SectionManager (scroll-spy). init NAO ativa: antes
  // ativava no boot e o contador flutuante (ICMS arrecadado) + o split do mapa
  // vazavam para a Tela "Mapa", fora da secao Impostometro. Fix 2026-05-31.
  init(): void {},
  activate(ctx: AppContext): void {
    if (!ctx.map) return;
    impostometroActiveMap = ctx.map;
    impostometroController.activate(ctx.map);
  },
  deactivate(): void {
    if (impostometroActiveMap) impostometroController.deactivate(impostometroActiveMap);
  },
};
